// French formatting helpers for RentaScan

export const eur = (v, decimals = 2) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(v));
};

export const eurHT = (v, decimals = 2) => `${eur(v, decimals)} HT`;
export const eurTTC = (v, decimals = 2) => `${eur(v, decimals)} TTC`;

export const pct = (v, decimals = 1) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return `${Number(v).toFixed(decimals).replace(".", ",")} %`;
};

export const num = (v, decimals = 2) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(v));
};

export const dateFR = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// « u » = 1 unité physique du produit (calcul de coût standard, cf. total_units).
// « Mesure » = repère de dosage informatif — n'impacte PAS le coût de la recette.
// « unité » reste reconnu en compat descendante mais n'est plus proposé dans l'UI.
export const UNITS = ["kg", "g", "L", "cl", "ml", "u", "Mesure", "bouteille", "botte", "carton"];

// Feb 2026 — Affichage utilisateur : « u » est présenté comme « pièce » dans
// tous les Select. La valeur stockée reste « u » (compat backend + tests).
export function unitLabel(u) {
  if (u === "u") return "pièce";
  return u;
}

// Neuf catégories canoniques — toute nouvelle classification (LLM, saisie,
// import CSV) doit atterrir dans l'une d'elles. Les anciennes catégories
// (« Charcuterie », « Fromagerie »…) sont automatiquement remappées via
// CATEGORY_ALIASES ci-dessous — jamais recréées comme filtre séparé.
export const CATEGORIES = [
  "Viandes",
  "Produits de la mer",
  "Fruits et légumes",
  "Crèmerie",
  "Épicerie",
  "Vins",
  "Spiritueux",
  "Boissons",
  "Divers",
];

// Ordre canonique d'affichage des filtres. « Tous » est ajouté côté UI.
export const CATEGORY_ORDER = [...CATEGORIES];

// Remappe les anciennes catégories vers l'une des 9 catégories canoniques
// afin que les produits déjà en base restent classés correctement.
export const CATEGORY_ALIASES = {
  "Viande": "Viandes",
  "Charcuterie": "Viandes",
  "Volaille": "Viandes",
  "Poisson": "Produits de la mer",
  "Poissons": "Produits de la mer",
  "Fruits & Légumes": "Fruits et légumes",
  "Fruits et Légumes": "Fruits et légumes",
  "Herbes aromatiques": "Fruits et légumes",
  "Fromagerie": "Crèmerie",
  "Crémerie": "Crèmerie",
  "Boulangerie": "Épicerie",
  "Sauces & Condiments": "Épicerie",
  "Condiments": "Épicerie",
  "Épicerie": "Épicerie",
  "Boisson": "Boissons",
  "Bières & Cidres": "Boissons",
  "Cocktails": "Spiritueux",
  "Alcools": "Spiritueux",
  "Emballages": "Divers",
  "Autre": "Divers",
};

export function canonicalCategory(cat) {
  if (!cat) return "Divers";
  if (CATEGORIES.includes(cat)) return cat;
  return CATEGORY_ALIASES[cat] || "Divers";
}
export const VAT_RATES = [
  { value: 5.5, label: "5,5 % (produits alimentaires)" },
  { value: 10, label: "10 % (restauration sur place)" },
  { value: 20, label: "20 % (alcool / boisson)" },
];

// 14 EU regulatory allergens (INCO 1169/2011)
export const ALLERGENS = [
  { code: "GLU", label: "Gluten",          desc: "Céréales contenant du gluten" },
  { code: "CRU", label: "Crustacés",       desc: "Crustacés et produits à base" },
  { code: "OEU", label: "Œufs",            desc: "Œufs et produits à base" },
  { code: "POI", label: "Poissons",        desc: "Poissons et produits à base" },
  { code: "ARA", label: "Arachides",       desc: "Arachides et produits à base" },
  { code: "SOJ", label: "Soja",            desc: "Soja et produits à base" },
  { code: "LAI", label: "Lait",            desc: "Lait et produits à base (lactose)" },
  { code: "FRU", label: "Fruits à coque",  desc: "Amandes, noisettes, noix..." },
  { code: "CEL", label: "Céleri",          desc: "Céleri et produits à base" },
  { code: "MOU", label: "Moutarde",        desc: "Moutarde et produits à base" },
  { code: "SES", label: "Sésame",          desc: "Graines de sésame" },
  { code: "SUL", label: "Sulfites",        desc: "Anhydride sulfureux / sulfites > 10 mg/kg" },
  { code: "LUP", label: "Lupin",           desc: "Lupin et produits à base" },
  { code: "MOL", label: "Mollusques",      desc: "Mollusques et produits à base" },
];
export const ALLERGEN_BY_CODE = Object.fromEntries(ALLERGENS.map((a) => [a.code, a]));
